import type {
  TokenExchangeResponse,
  AuthUser,
  LoginRequest,
  LoginResponse,
  VerifyOtpResponse,
  OtpSetupStartResponse,
  OtpSetupVerifyResponse,
  RefreshResponse,
  LogoutResponse,
  GetEmployersResponse,
  SelectEmployerResponse,
  SwitchEmployerResponse,
  MyEmployersResponse,
} from '@pm-next/shared-types';
import { apiClient } from './client';

export async function exchangeTokenApi(
  tenantToken: string,
  employerCode: string
): Promise<TokenExchangeResponse> {
  const response = await apiClient.post<TokenExchangeResponse>('/auth/exchange', {
    tenantToken,
    employerCode,
  });
  return response.data;
}

export async function login(email: string, password: string): Promise<LoginResponse> {
  const response = await apiClient.post<LoginResponse>('/auth/login', {
    email,
    password,
  } as LoginRequest);
  return response.data;
}

export async function verifyOtp(pendingToken: string, otp: string): Promise<VerifyOtpResponse> {
  const response = await apiClient.post<VerifyOtpResponse>('/auth/verify-otp', {
    pendingToken,
    otp,
  });
  return response.data;
}

export async function startOtpSetup(pendingToken: string): Promise<OtpSetupStartResponse> {
  const response = await apiClient.post<OtpSetupStartResponse>('/auth/otp-setup/start', {
    pendingToken,
  });
  return response.data;
}

export async function verifyOtpSetup(
  pendingToken: string,
  otp: string
): Promise<OtpSetupVerifyResponse> {
  const response = await apiClient.post<OtpSetupVerifyResponse>('/auth/otp-setup/verify', {
    pendingToken,
    otp,
  });
  return response.data;
}

export async function refresh(refreshToken: string): Promise<RefreshResponse> {
  const response = await apiClient.post<RefreshResponse>('/auth/refresh', { refreshToken });
  return response.data;
}

export async function logout(): Promise<LogoutResponse> {
  const response = await apiClient.post<LogoutResponse>('/auth/logout');
  return response.data;
}

export async function getCurrentUser(): Promise<AuthUser> {
  const response = await apiClient.get<AuthUser>('/auth/me');
  return response.data;
}

/** Fetch the current employer logo as a Blob (null if none). */
export async function fetchEmployerLogo(employerId: number): Promise<Blob | null> {
  try {
    // employerId query param busts HTTP cache per company (do not send custom
    // Cache-Control/Pragma — they are not in API CORS allowedHeaders).
    const response = await apiClient.get<Blob>('/auth/employer-logo', {
      responseType: 'blob',
      params: { employerId },
    });
    if (!response.data || response.data.size === 0) return null;
    if (response.data.type.includes('application/json')) return null;
    return response.data;
  } catch {
    return null;
  }
}

export async function getEmployers(pendingToken: string): Promise<GetEmployersResponse> {
  const response = await apiClient.post<GetEmployersResponse>('/auth/employers', {
    pendingToken,
  });
  return response.data;
}

export async function getMyEmployers(): Promise<MyEmployersResponse> {
  const response = await apiClient.get<MyEmployersResponse>('/auth/my-employers');
  return response.data;
}

export async function selectEmployer(
  pendingToken: string,
  employerId: number
): Promise<SelectEmployerResponse> {
  const response = await apiClient.post<SelectEmployerResponse>('/auth/select-employer', {
    pendingToken,
    employerId,
  });
  return response.data;
}

export async function switchEmployer(employerId: number): Promise<SwitchEmployerResponse> {
  const response = await apiClient.post<SwitchEmployerResponse>('/auth/switch-employer', {
    employerId,
  });
  return response.data;
}
